// ─────────────────────────────────────────────────────────────────────────────
// 🔑 PRE-CLOSING CHECKS (AFM v2, 2026-10-21 — backend/utils/preClosingVoe, routes/preClosingChecks).
//
// What this file needs before clear to close: the pre-closing VOE for each wage or 1099 earner (within the window
// before closing, a verbal VOE to an employer number Doorly looked up — never one the borrower supplied), the business
// existence check for each self-employed borrower, and by loan amount the desk review and the second appraisal. Staff
// record each one here; the Closing Module's clear-to-close step refuses until all have passed. Staff only.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('doorly_admin_token')}` } });

const LABELS = {
  voe: 'Pre-closing verification of employment',
  business_existence: 'Business existence',
  desk_review: 'Desk review of the appraisal',
  second_appraisal: 'Second independent appraisal',
};
const WORDS = {
  automated_database: 'Automated employment database', verbal_voe: 'Verbal VOE',
  employer_website: 'Employer website', public_directory: 'Public directory', state_business_registry: 'State business registry',
  third_party_database: 'Third-party database', other_independent: 'Other independent source',
  secretary_of_state: 'Secretary of State record', business_license: 'Business license',
  cpa_or_tax_preparer_letter: 'CPA or tax preparer letter', business_website_and_directory: 'Business website and directory listing',
  other_third_party: 'Other third-party source',
};
const input = { width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' };

export default function PreClosingChecksPanel({ userId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null);   // { type, borrowerId, result, method, phoneSource, employerPhone, contactName, appraisedValue, notes }
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/api/admin/borrowers/${userId}/pre-closing-checks`, auth())
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e?.response?.data?.error || 'Could not load the pre-closing checks.'));
  }, [userId]);
  useEffect(() => { if (userId) load(); }, [userId, load]);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      await axios.post(`${API}/api/admin/borrowers/${userId}/pre-closing-checks`, form, auth());
      setForm(null); load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not record the check.');
    } finally { setSaving(false); }
  };

  if (!data && !error) return null;
  const nameOf = (id) => {
    const b = (data?.borrowers || []).find((x) => Number(x.id) === Number(id));
    return b ? `${b.first_name || ''} ${b.last_name || ''}`.trim() : `#${id}`;
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="pre-closing-checks" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Pre-closing checks</div>
        {data && (
          <span data-testid="pre-closing-gate" style={{ fontSize: 12, fontWeight: 700, color: data.ok ? '#166534' : '#b45309' }}>
            {data.ok ? 'All passed — clear to close may proceed' : 'Required before clear to close'}
          </span>
        )}
      </div>
      {data && (
        <div style={{ fontSize: 12.5, color: '#6b7280', margin: '4px 0 10px', lineHeight: 1.5 }}>
          Employment is re-verified within {data.rules.daysBeforeClosing} days before closing
          {data.closingDate ? ` (closing ${data.closingDate})` : ' (no closing date set yet)'}. A verbal VOE uses an employer
          phone number {data.rules.phoneSource}.
        </div>
      )}
      {error && <div style={{ fontSize: 13, color: '#b91c1c', marginBottom: 8 }}>{error}</div>}

      {data && data.required.length === 0 && (
        <div style={{ fontSize: 13, color: '#6b7280' }}>No pre-closing checks apply to this file.</div>
      )}
      {data && data.required.map((req) => {
        const miss = data.missing.find((m) => m.type === req.type && (m.borrowerId ?? null) === (req.borrowerId ?? null));
        const key = `${req.type}-${req.borrowerId || 'file'}`;
        return (
          <div key={key} data-testid={`pre-closing-${key}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '8px 0', borderTop: '1px solid #f3f4f6', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: '#111827' }}>
                {LABELS[req.type]}{req.borrowerId ? ` — ${nameOf(req.borrowerId)}` : ''}
              </div>
              <div style={{ fontSize: 12, color: miss ? '#b45309' : '#166534' }}>{miss ? miss.text.split(': ').slice(1).join(': ') : 'Passed'}</div>
            </div>
            <button type="button" data-testid={`pre-closing-record-${key}`}
              onClick={() => setForm({ type: req.type, borrowerId: req.borrowerId, result: 'pass',
                method: req.type === 'voe' ? 'verbal_voe' : '', phoneSource: '', employerPhone: '', contactName: '', appraisedValue: '', notes: '' })}
              style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #3a6ad9', background: '#fff', color: '#3a6ad9', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
              Record
            </button>
          </div>
        );
      })}

      {form && (
        <div data-testid="pre-closing-form" style={{ marginTop: 10, padding: 12, background: '#f9fafb', borderRadius: 10, display: 'grid', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{LABELS[form.type]}{form.borrowerId ? ` — ${nameOf(form.borrowerId)}` : ''}</div>
          <label style={{ fontSize: 12, color: '#374151' }}>Result
            <select aria-label="Result" value={form.result} onChange={set('result')} style={input}>
              <option value="pass">Passed</option><option value="fail">Failed</option>
            </select>
          </label>
          {form.type === 'voe' && (
            <label style={{ fontSize: 12, color: '#374151' }}>How
              <select aria-label="How employment was verified" value={form.method} onChange={set('method')} style={input}>
                {data.options.voeMethods.map((m) => <option key={m} value={m}>{WORDS[m] || m}</option>)}
              </select>
            </label>
          )}
          {form.type === 'voe' && form.method === 'verbal_voe' && (
            <>
              <label style={{ fontSize: 12, color: '#374151' }}>Where the employer's phone number came from
                <select aria-label="Phone number source" value={form.phoneSource} onChange={set('phoneSource')} style={input}>
                  <option value="">Choose…</option>
                  {data.options.phoneSources.map((m) => <option key={m} value={m}>{WORDS[m] || m}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 12, color: '#374151' }}>Employer phone number called
                <input aria-label="Employer phone" value={form.employerPhone} onChange={set('employerPhone')} style={input} />
              </label>
              <label style={{ fontSize: 12, color: '#374151' }}>Who confirmed the employment
                <input aria-label="Employer contact" value={form.contactName} onChange={set('contactName')} style={input} />
              </label>
            </>
          )}
          {form.type === 'business_existence' && (
            <label style={{ fontSize: 12, color: '#374151' }}>How
              <select aria-label="How the business was verified" value={form.method} onChange={set('method')} style={input}>
                <option value="">Choose…</option>
                {data.options.businessMethods.map((m) => <option key={m} value={m}>{WORDS[m] || m}</option>)}
              </select>
            </label>
          )}
          {form.type === 'second_appraisal' && (
            <label style={{ fontSize: 12, color: '#374151' }}>Second appraisal value (the lower of the two is used)
              <input aria-label="Second appraisal value" inputMode="numeric" value={form.appraisedValue} onChange={set('appraisedValue')} style={input} />
            </label>
          )}
          <label style={{ fontSize: 12, color: '#374151' }}>Notes
            <textarea aria-label="Notes" rows={2} value={form.notes} onChange={set('notes')} style={{ ...input, resize: 'vertical' }} />
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" data-testid="pre-closing-save" disabled={saving} onClick={save}
              style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#3a6ad9', color: '#fff', fontWeight: 600, fontSize: 13, cursor: saving ? 'wait' : 'pointer' }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => setForm(null)}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
